import { Form } from "antd"
import BaseModal from "../../../components/layout/BaseModal"
import BaseInputSelection from "../../../components/elements/BaseInputSelection"
import BaseInputField from "../../../components/elements/BaseInputField"
import BaseSelection from "../../../components/elements/BaseSelection"
import { GeneralQueryKeys } from "../../../api/constants"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { AbsencePost } from "../types"
import { useAddAbsenceMutation } from "../api/services"
import { Employee } from "../../Mitarbeiter/types/Types"
import { getEmployeeNamesOnly } from "../../Mitarbeiter/api/apiCalls"
import { useDelayWhenSearchWithPagination } from "../../../services/functions"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"

type AddAbsenceModalType = {
    modalId: number
    closeModal: () => void
    queryKey: string
    userIsEmployee: boolean
    userSelf: Employee
}

export default function AddAbsenceModal(props: AddAbsenceModalType) {
    const [form] = Form.useForm()
    const [employeeSearch, setEmployeeSearch] = useState("")
    const searchParams = useDelayWhenSearchWithPagination(employeeSearch)
    // Mitarbeiter ohne abwesenheiten_read_write sehen keine Felder für Mitarbeiter und Typ: Antrag immer für sich selbst als Urlaub
    const initialAbsence = (): AbsencePost =>
        props.userIsEmployee && props.userSelf?._id !== undefined ? ({ employee: props.userSelf._id as string, type: "Urlaub" } as AbsencePost) : ({} as AbsencePost)
    const [newAbsence, setNewAbsence] = useState<AbsencePost>(initialAbsence)
    const { data: employees } = useQuery({
        queryKey: [GeneralQueryKeys.EMPLOYEE_NAMES_ONLY + searchParams],
        queryFn: () => getEmployeeNamesOnly(searchParams),
    })

    // Add absence
    const queryClient = useQueryClient()
    const { mutate: addAbsence } = useAddAbsenceMutation(queryClient, props.queryKey)
    const handleAddAbsence = () => {
        addAbsence(newAbsence)
        handleResetForm()
        props.closeModal()
    }

    // Reset form and newAbsence
    const handleResetForm = () => {
        form.resetFields()
        setNewAbsence(initialAbsence())
    }

    // FRONTEND LOGIC - Vorbelegung eigener Mitarbeiter + Urlaub, sobald der eigene Datensatz geladen ist
    // (Mitarbeiter: Pflicht, siehe initialAbsence; Admin/NL: wie bisher nur Vorbelegung, Felder änderbar)
    useEffect(() => {
        if (props.userSelf?._id !== undefined) {
            setNewAbsence((prev: AbsencePost) => ({
                ...prev,
                employee: props.userSelf?._id as string,
                type: "Urlaub",
            }))
        }
    }, [props.userIsEmployee, props.userSelf])

    return (
        <BaseModal
            size={props.userIsEmployee}
            onClose={() => handleResetForm()}
            modalId={props.modalId}
            title="Abwesenheit hinzufügen"
            bottomRow={
                <div className="flex w-full justify-end">
                    <button className="btn btn-primary" onClick={() => form.submit()}>
                        {!newAbsence?.type ? "Daten ausfüllen" : newAbsence?.type === "Urlaub" ? "Urlaub beantragen" : "Krankheit hinzufügen"}
                    </button>
                </div>
            }>
            <Form form={form} onFinish={handleAddAbsence}>
                <Authorized allOf={[P.abwesenheiten_read_write]}>
                    <Form.Item className="mb-2 mt-2" name="employee">
                        <BaseInputSelection
                            label="Mitarbeiter"
                            placeholder="Mitarbeiter auswählen"
                            data={employees ?? []}
                            setId={(e) => {
                                form.setFieldsValue({ employee: e })
                                setNewAbsence((prev: AbsencePost) => ({
                                    ...prev,
                                    employee: e,
                                }))
                            }}
                            onSearch={(e: string) => setEmployeeSearch(e)}
                        />
                    </Form.Item>
                </Authorized>
                <Authorized allOf={[P.abwesenheiten_read_write]}>
                    <Form.Item className="mb-2" name="type">
                        <BaseSelection
                            label="Typ"
                            placeholder="Typ auswählen"
                            data={["Urlaub", "Krankheit"]}
                            setId={(e) => {
                                form.setFieldsValue({ type: e })
                                setNewAbsence((prev: AbsencePost) => ({
                                    ...prev,
                                    type: e,
                                }))
                            }}
                        />
                    </Form.Item>
                </Authorized>
                <Form.Item className={`mb-2 ${props.userIsEmployee ? "mt-2" : ""}`} name="startDate">
                    <BaseInputField
                        label="Startdatum"
                        placeholder="Startdatum auswählen"
                        type={"date"}
                        onChange={(e: string) => {
                            form.setFieldsValue({ startDate: e })
                            setNewAbsence((prev: AbsencePost) => ({
                                ...prev,
                                startDate: new Date(e),
                            }))
                        }}
                    />
                </Form.Item>
                <Form.Item className="mb-2" name="endDate">
                    <BaseInputField
                        label="Enddatum"
                        placeholder="Enddatum auswählen"
                        type={"date"}
                        onChange={(e: string) => {
                            form.setFieldsValue({ endDate: e })
                            setNewAbsence((prev: AbsencePost) => ({
                                ...prev,
                                endDate: new Date(e),
                            }))
                        }}
                    />
                </Form.Item>
                <Form.Item className="mb-2" name="comment">
                    <BaseInputField
                        label="Notiz"
                        placeholder="Notiz eingeben"
                        type={"text"}
                        onChange={(e: string) => {
                            form.setFieldsValue({ comment: e })
                            setNewAbsence((prev: AbsencePost) => ({
                                ...prev,
                                comment: e,
                            }))
                        }}
                    />
                </Form.Item>
            </Form>
        </BaseModal>
    )
}
